import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import TimelineReportsService from '_services/timeline-reports-service';

import { extractFlightNotes, extractTaskNotes, formatJourneyName, getJavaScriptDate, newProcessName } from '_utils/functions';
import { PRMCodes, TARGET_CODES } from '_utils/constants';
import { getIataTimeZone } from '_utils/iata';

//#region async thunks

// Get passenger records report
export const getPassengerRecordsReport = createAsyncThunk('timelineReports/getPassengerRecordsReport', async (payload, { signal }) => {
  return await TimelineReportsService.getPassengerRecordsReportService(payload, signal);
}
);

// get passenger breakdown
export const getPassengerBreakdown = createAsyncThunk('timelineReports/getPassengerBreakdown', async (payload, { signal }) => {
  return await TimelineReportsService.getPassengerBreakdownService(payload, signal);
}
);

// batch at-loc re-check for portal
export const batchAtLocationCheck = createAsyncThunk('timelineReports/batchAtLocationCheck', async (payload) => {
  return await TimelineReportsService.batchAtLocationCheckService(payload);
});

//#endregion

//#region initial state
const initialState = {
  // selected report type
  selectedReportType: 'passenger-breakdown',

  // departure/arrival selection
  selectedDepartureArrival: 'Departures',

  // include no shows toggle
  includeNoShows: false,

  // include assistance breakdowns
  includeAssistanceBreakdown: false,

  // date range
  startDate: new Date(),
  endDate: new Date(),

  // passenger records report
  passengerRecordsReport: [],
  getPassengerRecordsReportStatus: 'idle',
  getPassengerRecordsReportError: null,

  showPaxJourney: false,
  hideNotServed: false,
  showOnlyNotServed: false,

  // batch at-loc re-check
  batchCheckResults: {},    // { [taskId]: { found, time, gpsFound, beaconFound } }
  batchCheckStatus: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
  batchCheckLoadingByTask: {},
  selectedAirline: null,

  //To remove when we do this on server side
  totalJourney: 0,
  totalPassengers: 0
};
//#endregion

//#region slice
const timelineReportsSlice = createSlice({
  name: 'timelineReports',
  initialState,
  reducers: {
    reset: state => initialState,
    // set selected report type
    setSelectedReportType: (state, action) => {
      state.selectedReportType = action.payload;
    },

    // set departure/arrival selection
    setSelectedDepartureArrival: (state, action) => {
      state.selectedDepartureArrival = action.payload;
    },

    // set include no shows toggle
    setIncludeNoShows: (state, action) => {
      state.includeNoShows = action.payload;
    },

    // set include assistance breakdowns toggle
    setIncludeAssistanceBreakdown: (state, action) => {
      state.includeAssistanceBreakdown = action.payload;
    },

    // set date range
    setDateRange: (state, action) => {
      state.startDate = action.payload.startDate;
      state.endDate = action.payload.endDate;
    },

    // reset passenger records report
    resetPassengerRecordsReport: (state) => {
      state.passengerRecordsReport = [];
      state.getPassengerRecordsReportStatus = 'idle';
      state.getPassengerRecordsReportError = null;
    },

    // set show pax journey toggle
    setShowPaxJourney: (state, action) => {
      state.showPaxJourney = action.payload;
    },

    setSelectedAirline: (state, action) => {
      state.selectedAirline = action.payload;
    },

    setHideNotServed: (state, action) => {
      state.hideNotServed = action.payload;
      // mutually exclusive with showOnlyNotServed
      if (action.payload) state.showOnlyNotServed = false;
    },

    setShowOnlyNotServed: (state, action) => {
      state.showOnlyNotServed = action.payload;
      // mutually exclusive with hideNotServed
      if (action.payload) state.hideNotServed = false;
    },

    clearBatchCheckResults: (state) => {
      state.batchCheckResults = {};
      state.batchCheckStatus = 'idle';
    },

  },
  extraReducers: (builder) => {
    builder
      // passenger records report
      .addCase(getPassengerRecordsReport.pending, (state) => {
        //console.log('Redux: getPassengerRecordsReport.pending');
        state.getPassengerRecordsReportStatus = 'loading';
        state.getPassengerRecordsReportError = null;
      })
      .addCase(getPassengerRecordsReport.fulfilled, (state, action) => {
        //console.log('Redux: getPassengerRecordsReport.fulfilled', action.payload);
        state.getPassengerRecordsReportStatus = 'succeeded';
        state.passengerRecordsReport = action.payload;
        state.getPassengerRecordsReportError = null;
      })
      .addCase(getPassengerRecordsReport.rejected, (state, action) => {
        //console.log('Redux: getPassengerRecordsReport.rejected', action.error);
        state.getPassengerRecordsReportStatus = 'failed';
        state.getPassengerRecordsReportError = action.error.message;

        // clear cache
        state.passengerRecordsReport = [];
      })
      // passenger breakdown
      .addCase(getPassengerBreakdown.pending, (state) => {
        //console.log('Redux: getPassengerBreakdown.pending');
        state.getPassengerRecordsReportStatus = 'loading';
        state.getPassengerRecordsReportError = null;
      })
      .addCase(getPassengerBreakdown.fulfilled, (state, action) => {
        //console.log('Redux: getPassengerBreakdown.fulfilled', action.payload);
        state.getPassengerRecordsReportStatus = 'succeeded';
        state.passengerRecordsReport = action.payload;
        state.getPassengerRecordsReportError = null;
      })
      .addCase(getPassengerBreakdown.rejected, (state, action) => {
        //console.log('Redux: getPassengerBreakdown.rejected', action.error);
        state.getPassengerRecordsReportStatus = 'failed';
        state.getPassengerRecordsReportError = action.error.message;
      })
      // batch at-loc re-check, append to each task when performing batch
      .addCase(batchAtLocationCheck.pending, (state, action) => {
        state.batchCheckStatus = 'loading';
        const tasks = action.meta.arg?.tasks || [];
        tasks.forEach(t => {
          state.batchCheckLoadingByTask[t.taskId] = true;
        });
      })

      .addCase(batchAtLocationCheck.fulfilled, (state, action) => {
        state.batchCheckStatus = 'succeeded';

        const results = action.payload?.results ?? [];
        const map = {};

        results.forEach(r => {
          map[r.taskId] = r;
          state.batchCheckLoadingByTask[r.taskId] = false;
        });

        state.batchCheckResults = { ...state.batchCheckResults, ...map };
      })

      .addCase(batchAtLocationCheck.rejected, (state, action) => {
        const tasks = action.meta.arg?.tasks || [];
        tasks.forEach(t => {
          state.batchCheckLoadingByTask[t.taskId] = false;
        });
        state.batchCheckStatus = 'failed';
      })
  }
});
//#endregion

//#region selectors

// selected report type
export const selectSelectedReportType = (state) => state.timelineReports.selectedReportType;

// departure/arrival selection
export const selectSelectedDepartureArrival = (state) => state.timelineReports.selectedDepartureArrival || "Departures";

// include no shows toggle
export const selectIncludeNoShows = (state) => state.timelineReports.includeNoShows;

// include assistance breakdowns toggle
export const selectIncludeAssistanceBreakdown = (state) => state.timelineReports.includeAssistanceBreakdown;

// date range
export const selectStartDate = (state) => state.timelineReports.startDate;
export const selectEndDate = (state) => state.timelineReports.endDate;

// passenger records report
export const selectPassengerRecordsReport = (state) => state.timelineReports.passengerRecordsReport;
export const selectPassengerRecordsReportStatus = (state) => state.timelineReports.getPassengerRecordsReportStatus;
export const selectGetPassengerRecordsReportError = (state) => state.timelineReports.getPassengerRecordsReportError;

// passenger breakdown
export const selectPassengerBreakdown = (state) => state.timelineReports.passengerRecordsReport;
export const selectPassengerBreakdownStatus = (state) => state.timelineReports.getPassengerRecordsReportStatus;
export const selectGetPassengerBreakdownError = (state) => state.timelineReports.getPassengerRecordsReportError;

// show pax journey toggle
export const selectShowPaxJourney = (state) => state.timelineReports.showPaxJourney;
export const selectSelectedAirline = (state) => state.timelineReports.selectedAirline;

// hide not served toggle
export const selectHideNotServed = (state) => state.timelineReports.hideNotServed;

// show only not served toggle
export const selectShowOnlyNotServed = (state) => state.timelineReports.showOnlyNotServed;

// \b works because codes are alnum; they’re delimited by spaces, dots, slashes, etc.
//Discarde ENDPAL as it's part of sita message not PRM Code
const PRM_REGEX = new RegExp(`\\b(?:${PRMCodes.join('|')})\\b`, 'g');
const TARGET_SET = new Set(TARGET_CODES);
// For a single side (inbound OR outbound) tally counts by TARGET_CODES + OTHER
const extractPrmsFromRow = (list) => {
  const counts = Object.fromEntries(TARGET_CODES.map(c => [c, 0]));
  counts.OTHER = 0;

  (list || []).forEach(item => {
    const n = Number(item?.passengerCount) || 0;
    if (!n) return;

    const codeStr = String(item?.assistanceCode || '').toUpperCase();
    const matches = codeStr.match(PRM_REGEX) || [];
    if (matches.length === 0) return; // no PRM code -> ignore completely

    const uniq = Array.from(new Set(matches));
    let matchedTarget = false;

    uniq.forEach(code => {
      if (TARGET_SET.has(code)) {
        counts[code] += n;
        matchedTarget = true;
      }
    });

    // if there were PRM codes but none of them are TARGET codes -> OTHER
    if (!matchedTarget) counts.OTHER += n;
  });

  return counts;
}

const sumCounts = (a, b) => {
  const out = {};
  Object.keys(a).forEach(k => { out[k] = (a[k] || 0) + (b[k] || 0); });
  return out;
};

// check if obj is valid date
function isValidDate(value) {
  const d = new Date(value);
  return d instanceof Date && !isNaN(d);
}

// format the date using a shared Intl.DateTimeFormat instance
function formatIfDate(value, formatter) {
  if (!value) return 'Not Served';

  if (isValidDate(value)) {
    return formatter.format(new Date(value));
  }

  // if not valid date - return value as it is
  return value;
}

// data + columns for table
export const selectPassengerBreakdownDataAndColumns = createSelector(
  [selectPassengerRecordsReport, selectIncludeAssistanceBreakdown],
  (report, includeAssistanceBreakdown) => {
    if (!report || !report.dailyBreakdown || !Array.isArray(report.dailyBreakdown)) {
      return { columns: [], data: [] };
    }

    const columns = [
      { name: 'Date', key: 'date' },
      { name: 'Arrivals', key: 'totalInboundPassengers' },
      { name: 'Departures', key: 'totalOutboundPassengers' },
      { name: 'Total', key: 'total' },
    ];

    // assistance codes to show
    const showAssist = !!includeAssistanceBreakdown;

    if (showAssist) {
      TARGET_CODES.forEach(code => {
        columns.push({ name: code, key: code }); // key used to identify column
      });
      columns.push({ name: 'OTHER', key: 'OTHER' });
    }


    const data = report.dailyBreakdown.map(entry => {
      const row = [
        entry.date || '',
        entry.summary?.totalInboundPassengers || 0,
        entry.summary?.totalOutboundPassengers || 0,
        (entry.summary?.totalInboundPassengers || 0) + (entry.summary?.totalOutboundPassengers || 0),
      ];

      if (showAssist) {
        const inboundCounts = extractPrmsFromRow(entry.assistanceBreakdown?.inbound);
        const outboundCounts = extractPrmsFromRow(entry.assistanceBreakdown?.outbound);
        const combined = sumCounts(inboundCounts, outboundCounts);

        // push TARGET columns in the same order, then OTHER
        TARGET_CODES.forEach(code => row.push(combined[code] || 0));
        row.push(combined.OTHER || 0);
      }

      return row;
    });

    return { columns, data };
  }
);

// summary for cards
export const selectPassengerBreakdownSummary = (state) => {
  const report = state.timelineReports.passengerRecordsReport;
  return report?.overallSummary || null;
}

// aggregate assistance totals across every day in the range (TARGET_CODES + OTHER)
export const selectPassengerBreakdownAssistanceTotals = createSelector(
  [selectPassengerRecordsReport, selectIncludeAssistanceBreakdown],
  (report, includeAssistanceBreakdown) => {
    if (!includeAssistanceBreakdown) return null;
    if (!report || !Array.isArray(report.dailyBreakdown) || report.dailyBreakdown.length === 0) {
      return null;
    }

    // seed zeroed totals for TARGET_CODES + OTHER
    let totals = { ...Object.fromEntries(TARGET_CODES.map(c => [c, 0])), OTHER: 0 };

    report.dailyBreakdown.forEach(entry => {
      const inbound = extractPrmsFromRow(entry.assistanceBreakdown?.inbound);
      const outbound = extractPrmsFromRow(entry.assistanceBreakdown?.outbound);
      totals = sumCounts(totals, sumCounts(inbound, outbound));
    });

    const grandTotal = Object.values(totals).reduce((a, b) => a + b, 0);

    return { totals, grandTotal, days: report.dailyBreakdown.length };
  }
);

// processed passenger records report with columns and data
const getFilteredProcessedBookings = (report, airlineSelected, hideNotServed, showOnlyNotServed) => {
  const switchedAirline = airlineSelected === "EZY" ? "U2" : airlineSelected
  if (!report || !report.processedBookings || !Array.isArray(report.processedBookings)) {
    return [];
  }

  return report.processedBookings.filter((booking) => {
    if (airlineSelected && !String(booking.flightNumber || '').includes(switchedAirline)) {
      return false;
    }
    if (hideNotServed && booking.allocated === 'Not Served') {
      return false;
    }
    if (showOnlyNotServed && booking.allocated !== 'Not Served') {
      return false;
    }
    return true;
  });
};

// iata argument input selector for the records data selector
const selectIataArg = (_state, iata) => iata;

// memoized filtered bookings shared by the data + summary selectors
const selectFilteredProcessedBookings = createSelector(
  [selectPassengerRecordsReport, selectSelectedAirline, selectHideNotServed, selectShowOnlyNotServed],
  (report, airlineSelected, hideNotServed, showOnlyNotServed) =>
    getFilteredProcessedBookings(report, airlineSelected, hideNotServed, showOnlyNotServed)
);

export const selectPassengerRecordsReportDataAndColumns = createSelector(
  [
    selectPassengerRecordsReport,
    selectFilteredProcessedBookings,
    selectSelectedDepartureArrival,
    selectShowPaxJourney,
    selectIataArg,
  ],
  (report, processedBookings, selectedDepartureArrival, showPaxJourney, iata) => {
    const airportsWithAmbilift = ["PUJ"]
    const normalizedIata = String(iata ?? '').toUpperCase();
    const includeAmbiliftRequiredColumn = airportsWithAmbilift.includes(normalizedIata);
    const airportTz = getIataTimeZone(normalizedIata);
    const timeFormatOpts = {
      hour: '2-digit',
      minute: '2-digit',
      ...(airportTz ? { timeZone: airportTz } : {}),
    };
    // single shared formatter instead of constructing one per cell
    const timeFmt = new Intl.DateTimeFormat('en-GB', timeFormatOpts);
    if (!report || !report.processedBookings || !Array.isArray(report.processedBookings)) {
      return { columns: [], data: [] };
    }

    // Base columns that are always shown
    const baseColumns = [
      { name: 'Passenger Name', key: 'name' },
      { name: 'Assistance', key: 'assistance' },
      { name: 'Flight Number', key: 'flightNumber' },
      { name: 'Pre-Advised', key: 'preAdvised' },
      { name: 'Airport', key: 'airport' },
    ];

    // Add time columns based on filter
    const timeColumns = [];
    if (selectedDepartureArrival === 'Departures') {
      // For departures, show STD and ATD
      timeColumns.push(
        { name: 'STD', key: 'std' },
        { name: 'ATD', key: 'atd' }
      );
    } else if (selectedDepartureArrival === 'Arrivals') {
      // For arrivals, show STA and ATA
      timeColumns.push(
        { name: 'STA', key: 'sta' },
        { name: 'ATA', key: 'ata' }
      );
    } else {
      // For 'All', show all four time columns (each row fills only its relevant pair)
      timeColumns.push(
        { name: 'STD', key: 'std' },
        { name: 'ATD', key: 'atd' },
        { name: 'STA', key: 'sta' },
        { name: 'ATA', key: 'ata' }
      );
    }

    // Final columns
    const columns = [
      ...baseColumns,
      ...timeColumns,
      { name: 'Created', key: 'dateCreated' },
      { name: 'Allocated', key: 'allocated' },
      { name: 'Finished', key: 'finished' },
      { name: 'Agent', key: 'agent' },
      { name: 'Comments', key: 'comments' },
      { name: 'Flight Notes', key: 'flightNotes' },
      ...(includeAmbiliftRequiredColumn ? [{ name: 'Ambilift Required', key: 'ambiliftRequired' }] : [])
    ];
    if (showPaxJourney) columns.push({ name: 'Number of Journeys', key: 'numberOfJourney' });

    const formatTimeCell = (val) => (val ? timeFmt.format(new Date(val)) : '');

    const data = processedBookings.map(booking => {

      // Base data that's always included
      const baseData = [
        newProcessName(booking.name) || '',
        booking.assistance || '',
        booking.flightNumber || '',
        booking.preAdvised ? 'Yes' : 'No',
        booking.airport || ''
      ];

      // Add time data based on filter
      const timeData = [];
      if (selectedDepartureArrival === 'Departures') {
        // For departures, show STD and ATD
        timeData.push(formatTimeCell(booking.std), formatTimeCell(booking.atd));
      } else if (selectedDepartureArrival === 'Arrivals') {
        // For arrivals, show STA and ATA
        timeData.push(formatTimeCell(booking.sta), formatTimeCell(booking.ata));
      } else {
        // For 'All', show all four (each row only has its relevant pair populated)
        timeData.push(
          formatTimeCell(booking.std),
          formatTimeCell(booking.atd),
          formatTimeCell(booking.sta),
          formatTimeCell(booking.ata)
        );
      }

      // Final data row
      let finalData = [
        ...baseData,
        ...timeData,
        formatTimeCell(booking.dateCreated),
        formatIfDate(booking.allocated, timeFmt), // shows allocated time OR "rejected pax"
        formatIfDate(booking.finished, timeFmt), // shows finished time OR "rejected pax"
        booking.agent || '',
        booking.comments || '',
        extractFlightNotes(booking.flightNotes),
      ];
      if (includeAmbiliftRequiredColumn) finalData.push(booking.ambiliftRequired ? "True" : "False");
      if (showPaxJourney) {
        // if show pax journey is switched on, make children columns and data from the total journey
        const childrenColumns = [
          { name: 'ID', key: '_id' },
          { name: 'Type', key: 'type' },
          { name: 'Check-in', key: 'checkInTime' },
          { name: 'Allocated Time', key: 'allocatedTime' },
          { name: 'Allocated By', key: 'allocatedBy' },
          { name: 'Pick Up', key: 'pickup' },
          { name: 'Drop Off', key: 'dropoff' },
          { name: 'System Gate', key: 'systemGate' },
          { name: 'Confirm Time', key: 'confirmTime' },
          { name: "At Loc Time", key: "atLocTime" },
          { name: "At Loc Verified By", key: "atLocVerifiedBy" },
          { name: 'Start Time', key: 'startTime' },
          { name: 'Complete Time', key: 'completeTime' },
          { name: 'Agent', key: 'agent' },
          { name: 'Comments', key: 'comments' },
          { name: 'Task Vehicle', key: 'taskVehicle' },
          { name: 'Status', key: 'status' },
        ];

        const formattedChildrenData = Array.isArray(booking.totalJourney) ? booking.totalJourney.map(journey => {
          const atLocInfo = journey?.atLocationInfo;
          let atLocVerifiedBy = 'N/A';
          if (atLocInfo) {
            if (atLocInfo.beaconVerified) atLocVerifiedBy = 'Beacon';
            else if (atLocInfo.qrVerified) atLocVerifiedBy = 'QR';
            else if (atLocInfo.geofenceVerified) atLocVerifiedBy = 'Geofence';
            else if (atLocInfo.type) atLocVerifiedBy = atLocInfo.type;
          }

          let status = 'Active';
          if (journey?.completeTime) status = 'Completed';
          if (journey?.taskCancelled) status = 'Rejected';
          if (journey?.journey?.startsWith?.('transferred')) status = 'Transferred';

          const childRow = [
            journey?.taskId ?? "N/A",
            formatJourneyName(journey?.journey) ?? "N/A",
            journey?.checkInTime ? timeFmt.format(new Date(journey.checkInTime)) : 'N/A',
            journey?.allocatedTime ? timeFmt.format(getJavaScriptDate(journey.allocatedTime)) : 'N/A',
            journey?.assignedBy?.name ?? "N/A",
            journey?.pickUpLocation?.name ?? "N/A",
            journey?.dropOffLocation?.name ?? "N/A",
            journey?.allocatedDropOffLocation?.name ?? "N/A",
            journey?.acknowledgedTime ? timeFmt.format(getJavaScriptDate(journey.acknowledgedTime)) : 'N/A',
            journey?.atLocationTime ? timeFmt.format(getJavaScriptDate(journey.atLocationTime)) : 'N/A',
            atLocVerifiedBy,
            journey?.startTime ? timeFmt.format(getJavaScriptDate(journey.startTime)) : 'N/A',
            journey?.completeTime ? timeFmt.format(getJavaScriptDate(journey.completeTime)) : 'N/A',
            journey?.employeeName ?? "N/A",
            extractTaskNotes(journey?.taskNotes),
            journey?.startedWithEquipment ?? "No Vehicle",
            status,
          ];


          return childRow;
        }) : [];

        finalData.push({
          value: booking?.totalJourney?.length ?? 0,
          childrenColumns: childrenColumns,
          childrenData: formattedChildrenData,
        });
      }
      // attach flight info as a named property (not an indexed cell so it isn't rendered as a column)
      finalData.flightInfo = booking.flightInfo ?? null;
      // attach location history + handover legs (same named-property pattern) so the
      // table can expand them without adding columns
      finalData.locationHistory = Array.isArray(booking.locationHistory) ? booking.locationHistory : [];
      finalData.handoverJourney = Array.isArray(booking.handoverJourney) ? booking.handoverJourney : [];
      return finalData;
    });

    return { columns, data };
  }
);

export const selectPassengerRecordsReportSummary = createSelector(
  [
    selectPassengerRecordsReport,
    selectFilteredProcessedBookings,
    selectSelectedAirline,
    selectHideNotServed,
    selectShowOnlyNotServed,
  ],
  (report, processedBookings, airlineSelected, hideNotServed, showOnlyNotServed) => {
    if (!report || !report.summary) return null;
    if (!airlineSelected && !hideNotServed && !showOnlyNotServed) return report.summary;

    const totalPassengers = processedBookings.length;
    const totalJourneys = processedBookings.reduce((sum, booking) => sum + (Array.isArray(booking.totalJourney) ? booking.totalJourney.length : 0), 0);

    return {
      ...report.summary,
      totalPassengers,
      totalJourneys,
    };
  }
);

// batch at-loc re-check selectors
export const selectBatchCheckResults = (state) => state.timelineReports.batchCheckResults;
export const selectBatchCheckStatus = (state) => state.timelineReports.batchCheckStatus;
export const selectBatchCheckLoadingByTask = (state) =>
  state.timelineReports.batchCheckLoadingByTask;
//#endregion

//#region exports
export const {
  setSelectedReportType,
  setSelectedDepartureArrival,
  setIncludeNoShows,
  setDateRange,
  resetPassengerRecordsReport,
  setIncludeAssistanceBreakdown,
  reset,
  setShowPaxJourney,
  setHideNotServed,
  setShowOnlyNotServed,
  clearBatchCheckResults,
  setSelectedAirline,
} = timelineReportsSlice.actions;

export default timelineReportsSlice.reducer;
//#endregion